import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, CheckCircle2, XCircle, Clock, FileText, Loader2 } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { EmptyState } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";

const STATUS = {
  pending: { icon: Clock, cls: "border-amber-200 bg-amber-50 text-amber-800" },
  fulfilled: { icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  declined: { icon: XCircle, cls: "border-red-200 bg-red-50 text-red-700" },
};

export default function Requests() {
  const { company } = useAuth();
  const [requests, setRequests] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // request being fulfilled
  const [declineFor, setDeclineFor] = useState(null);
  const [docId, setDocId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!company) return;
    setLoading(true);
    try {
      const [r, d] = await Promise.all([
        api.get(`/companies/${company.id}/document-requests`),
        api.get(`/companies/${company.id}/documents`),
      ]);
      setRequests(r.data); setDocs(d.data);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [company]);

  const fulfill = async () => {
    setBusy(true);
    try {
      await api.put(`/document-requests/${active.id}/fulfill`, { document_id: docId || null, note });
      toast.success("Request fulfilled" + (docId ? " and document shared" : ""));
      setActive(null); setDocId(""); setNote(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.put(`/document-requests/${declineFor.id}/decline`, { reason: note });
      toast.success("Request declined");
      setDeclineFor(null); setNote(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const pending = requests.filter((r) => r.status === "pending");
  const resolved = requests.filter((r) => r.status !== "pending");

  const RequestCard = ({ r }) => {
    const st = STATUS[r.status] || STATUS.pending;
    return (
      <Card className="rounded-xl border bg-card p-4" data-testid={`request-${r.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">{r.category}</span>
              <Badge className={`gap-1 ${st.cls}`}><st.icon className="h-3 w-3" />{r.status}</Badge>
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">{r.investor_name} · {r.investor_email} · {formatDate(r.created_at)}</div>
            {r.message && <p className="mt-2 rounded-lg bg-secondary/60 p-2 text-sm text-foreground">“{r.message}”</p>}
            {r.resolution_note && <p className="mt-1 text-xs text-muted-foreground">Your note: {r.resolution_note}</p>}
          </div>
          {r.status === "pending" && (
            <div className="flex shrink-0 gap-2">
              <Button size="sm" onClick={() => setActive(r)} data-testid={`fulfill-${r.id}`}>Fulfill</Button>
              <Button size="sm" variant="outline" onClick={() => setDeclineFor(r)} data-testid={`decline-${r.id}`}>Decline</Button>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <AppShell title="Document Requests" subtitle="Investor requests, aligned to their information rights">
      {loading ? <p className="text-sm text-muted-foreground">Loading...</p> : requests.length === 0 ? (
        <EmptyState icon={Inbox} title="No requests" description="When investors request documents allowed by their information rights, they show up here." />
      ) : (
        <div className="space-y-6">
          <div>
            <h2 className="mb-2 text-sm font-medium text-muted-foreground">Pending ({pending.length})</h2>
            <div className="space-y-2">{pending.length === 0 ? <p className="text-sm text-muted-foreground">Nothing pending.</p> : pending.map((r) => <RequestCard key={r.id} r={r} />)}</div>
          </div>
          {resolved.length > 0 && (
            <div>
              <h2 className="mb-2 text-sm font-medium text-muted-foreground">Resolved</h2>
              <div className="space-y-2">{resolved.map((r) => <RequestCard key={r.id} r={r} />)}</div>
            </div>
          )}
        </div>
      )}

      {/* Fulfill dialog */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Fulfill request - {active?.category}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Optionally attach an existing document; it will be automatically shared with {active?.investor_name}.</p>
            <div>
              <Label>Attach document (optional)</Label>
              <Select value={docId} onValueChange={setDocId}>
                <SelectTrigger className="mt-1" data-testid="fulfill-doc"><SelectValue placeholder="Choose a document" /></SelectTrigger>
                <SelectContent>{docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} ({d.category})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Note (optional)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" data-testid="fulfill-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
            <Button onClick={fulfill} disabled={busy} className="gap-2" data-testid="fulfill-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Mark fulfilled</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={!!declineFor} onOpenChange={(v) => !v && setDeclineFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Decline request - {declineFor?.category}</DialogTitle></DialogHeader>
          <div><Label>Reason (shown to investor)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="e.g. This is outside the agreed information rights." data-testid="decline-reason" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineFor(null)}>Cancel</Button>
            <Button onClick={decline} disabled={busy} className="gap-2" data-testid="decline-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Decline</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
